/**
 * Extractor YAML, read lightly.
 *
 * The console never needs a full YAML parser for an extractor: the workbench
 * (docs/extractors-requirements.md W2) wants the OUTLINE — which streams it
 * writes, which first-matches it tries, which fields each mapping annotates
 * and translates — plus enough of the shape to (a) colour the parsed output's
 * fields by ORIGIN and (b) run a small demo simulation offline. Real
 * simulation is `parsers/play` on the backend; the offline runner only exists
 * so demo mode has a live preview (rule 6) and is labelled as such.
 *
 * Grammar read here, from the captured YAMLs (accops-hyworks 730, AI-GEN-TEST):
 *
 *   provides-streams:            - STREAM
 *   master-filters:              - <regex>
 *   event-details:
 *   - first-match: <regex>
 *     decoder: regex | json | kv
 *     decoder-regex: <regex with (?P<name>…) groups>
 *     event-key-format: '{action}'
 *     event-key-mapping:
 *       <key or regex>:
 *         annotate:  Field: value
 *         translate: raw: Field
 *     fallback:  annotate: …
 *     globals:   translate: …
 *
 * Indentation-based, two-space steps, values as plain scalars or quoted
 * strings. Anything outside that is kept as raw lines and ignored — the
 * outline is best-effort, never an error.
 */

import { SYSTEM_FIELDS, toJsRegex } from './extractor-regex'

export { SYSTEM_FIELDS, toJsRegex }

export interface KeyMapping {
  key: string
  annotate: Record<string, string>
  translate: Record<string, string>
}

export interface EventDetail {
  firstMatch: string | null
  decoder: string | null
  decoderRegex: string | null
  keyFormat: string | null
  mappings: KeyMapping[]
  fallback: { annotate: Record<string, string> } | null
  globals: { translate: Record<string, string> } | null
}

export interface ExtractorOutline {
  extractorId: string | null
  sourceName: string | null
  sourceType: string | null
  vendor: string | null
  product: string | null
  streams: string[]
  masterFilters: string[]
  events: EventDetail[]
}

interface Line {
  indent: number
  text: string
  n: number
}

function unquote(v: string): string {
  const t = v.trim()
  if ((t.startsWith("'") && t.endsWith("'")) || (t.startsWith('"') && t.endsWith('"'))) {
    return t.slice(1, -1).replace(/''/g, "'")
  }
  return t
}

function lines(yaml: string): Line[] {
  return yaml.split(/\r?\n/).map((raw, n) => {
    const text = raw.replace(/\s+$/, '')
    const indent = text.length - text.trimStart().length
    return { indent, text: text.trim(), n }
  }).filter((l) => l.text && !l.text.startsWith('#'))
}

/** `key: value` → [key, value]; `key:` → [key, '']; else null. */
function kv(text: string): [string, string] | null {
  const m = /^([^:#][^:]*?):(?:\s+(.*))?$/.exec(text)
  if (!m) return null
  return [m[1]!.trim(), (m[2] ?? '').trim()]
}

/** Collect `k: v` pairs indented deeper than `indent`, starting at line i. */
function block(ls: Line[], i: number, indent: number): { map: Record<string, string>; next: number } {
  const map: Record<string, string> = {}
  let j = i
  while (j < ls.length && ls[j]!.indent > indent) {
    const p = kv(ls[j]!.text)
    if (p && p[1]) map[unquote(p[0])] = unquote(p[1])
    j++
  }
  return { map, next: j }
}

export function parseOutline(yaml: string): ExtractorOutline {
  const ls = lines(yaml)
  const out: ExtractorOutline = {
    extractorId: null, sourceName: null, sourceType: null, vendor: null, product: null,
    streams: [], masterFilters: [], events: [],
  }
  let i = 0
  while (i < ls.length) {
    const l = ls[i]!
    if (l.indent === 0) {
      const p = kv(l.text)
      if (p) {
        const [k, v] = p
        if (k === 'extractor-id') out.extractorId = unquote(v)
        else if (k === 'source-name') out.sourceName = unquote(v)
        else if (k === 'source-type') out.sourceType = unquote(v)
        else if (k === 'vendor-name') out.vendor = unquote(v)
        else if (k === 'product-name') out.product = unquote(v)
        else if (k === 'provides-streams' || k === 'master-filters') {
          const target = k === 'provides-streams' ? out.streams : out.masterFilters
          i++
          while (i < ls.length && ls[i]!.text.startsWith('- ')) {
            target.push(unquote(ls[i]!.text.slice(2)))
            i++
          }
          continue
        } else if (k === 'event-details') {
          i++
          i = parseEvents(ls, i, out)
          continue
        }
      }
    }
    i++
  }
  return out
}

function parseEvents(ls: Line[], start: number, out: ExtractorOutline): number {
  let i = start
  let cur: EventDetail | null = null
  while (i < ls.length && (ls[i]!.indent > 0 || ls[i]!.text.startsWith('- '))) {
    const l = ls[i]!
    const isItem = l.text.startsWith('- ')
    const text = isItem ? l.text.slice(2) : l.text
    const p = kv(text)
    if (isItem && l.indent === 0) {
      cur = { firstMatch: null, decoder: null, decoderRegex: null, keyFormat: null, mappings: [], fallback: null, globals: null }
      out.events.push(cur)
    }
    if (!cur || !p) { i++; continue }
    const [k, v] = p
    const base = isItem ? l.indent + 2 : l.indent
    if (k === 'first-match') cur.firstMatch = unquote(v)
    else if (k === 'decoder') cur.decoder = unquote(v)
    else if (k === 'decoder-regex') cur.decoderRegex = unquote(v)
    else if (k === 'event-key-format') cur.keyFormat = unquote(v)
    else if (k === 'fallback') {
      const r = readSections(ls, i + 1, base)
      cur.fallback = { annotate: r.annotate }
      i = r.next
      continue
    } else if (k === 'globals') {
      const r = readSections(ls, i + 1, base)
      cur.globals = { translate: r.translate }
      i = r.next
      continue
    } else if (k === 'event-key-mapping') {
      i++
      while (i < ls.length && ls[i]!.indent > base) {
        const mp = kv(ls[i]!.text)
        if (mp && !mp[1]) {
          const r = readSections(ls, i + 1, ls[i]!.indent)
          cur.mappings.push({ key: unquote(mp[0]), annotate: r.annotate, translate: r.translate })
          i = r.next
        } else i++
      }
      continue
    }
    i++
  }
  return i
}

function readSections(ls: Line[], i: number, indent: number) {
  const annotate: Record<string, string> = {}
  const translate: Record<string, string> = {}
  let j = i
  while (j < ls.length && ls[j]!.indent > indent) {
    const p = kv(ls[j]!.text)
    if (p && !p[1] && (p[0] === 'annotate' || p[0] === 'translate')) {
      const b = block(ls, j + 1, ls[j]!.indent)
      Object.assign(p[0] === 'annotate' ? annotate : translate, b.map)
      j = b.next
    } else j++
  }
  return { annotate, translate, next: j }
}

/* --------------------------------------------------------------- origins */

export type FieldOrigin = 'annotate' | 'translate' | 'global' | 'fallback' | 'system'


/** Where each output field comes from, per the outline. Stream/EventName can be annotated too. */
export function fieldOrigins(o: ExtractorOutline): Map<string, FieldOrigin> {
  const m = new Map<string, FieldOrigin>()
  for (const e of o.events) {
    for (const mp of e.mappings) {
      for (const f of Object.keys(mp.annotate)) m.set(f, 'annotate')
      for (const f of Object.values(mp.translate)) if (!m.has(f)) m.set(f, 'translate')
    }
    if (e.globals) for (const f of Object.values(e.globals.translate)) if (!m.has(f)) m.set(f, 'global')
    if (e.fallback) for (const f of Object.keys(e.fallback.annotate)) if (!m.has(f)) m.set(f, 'fallback')
  }
  return m
}

export function originOf(field: string, origins: Map<string, FieldOrigin>): FieldOrigin {
  return origins.get(field) ?? (SYSTEM_FIELDS.has(field) ? 'system' : 'translate')
}

/* -------------------------------------------------------------- coverage */

export type CoverageState = 'all' | 'some' | 'never'

export interface StreamCoverage {
  stream: string
  /** DDM fields for the stream, in catalog order, with how often the samples set them. */
  fields: Array<{ field: string; state: CoverageState; setOn: number }>
  /** Fields the extractor sets that the DDM stream does not declare — the "new fields" the validator flags. */
  undeclared: string[]
  known: boolean
}

/**
 * Coverage feedback (W2): for each stream the extractor writes, which of the
 * stream's DDM fields the parsed rows actually populate. Answers "parsed or
 * just ingested" before saving. System fields the engine stamps are excluded
 * from the never-set list so they do not read as the author's omissions.
 */
export function coverageFor(
  o: ExtractorOutline,
  rows: Array<Record<string, unknown>>,
  ddm: Record<string, string[]>,
): StreamCoverage[] {
  const streams = o.streams.length ? o.streams : [...new Set(rows.map((r) => String(r.Stream ?? '')).filter(Boolean))]
  return streams.map((stream) => {
    const inStream = rows.filter((r) => String(r.Stream ?? '') === stream)
    const declared = ddm[stream]
    const known = declared !== undefined
    const setCount = (f: string) => inStream.filter((r) => r[f] !== undefined && r[f] !== null && r[f] !== '').length
    const fields = (declared ?? [])
      .filter((f) => !SYSTEM_FIELDS.has(f))
      .map((f) => {
        const n = setCount(f)
        const state: CoverageState = inStream.length === 0 ? 'never' : n === inStream.length ? 'all' : n > 0 ? 'some' : 'never'
        return { field: f, state, setOn: n }
      })
    const produced = new Set(inStream.flatMap((r) => Object.keys(r)))
    const undeclared = known ? [...produced].filter((f) => !declared!.includes(f) && !SYSTEM_FIELDS.has(f)).sort() : []
    return { stream, fields, undeclared, known }
  })
}

/* ------------------------------------------------------- demo simulation */

// The simulator lives with the rule analysis (extractor-events.ts, W5.3) and
// runs on the full model — json, key-value and subs included. Re-exported so
// the workbench and the tests keep their import.
export { simulateDemo } from './extractor-events'

/* ---------------------------------------------------------- highlighting */

export type YamlTokenKind = 'key' | 'value' | 'string' | 'dash' | 'comment' | 'number' | 'punct' | 'text'
export interface YamlToken { kind: YamlTokenKind; text: string }

/** One line → tokens. Colour only, never weight (the overlay must keep the textarea's advances). */
export function tokenizeYamlLine(line: string): YamlToken[] {
  const out: YamlToken[] = []
  let rest = line
  const lead = /^\s*/.exec(rest)![0]
  if (lead) { out.push({ kind: 'text', text: lead }); rest = rest.slice(lead.length) }
  if (rest.startsWith('#')) { out.push({ kind: 'comment', text: rest }); return out }
  if (rest.startsWith('- ')) { out.push({ kind: 'dash', text: '- ' }); rest = rest.slice(2) }
  else if (rest === '-') { out.push({ kind: 'dash', text: '-' }); return out }
  const m = /^([^:#'"][^:]*?):(\s+|$)/.exec(rest)
  if (m) {
    out.push({ kind: 'key', text: m[1]! }, { kind: 'punct', text: ':' + m[2]! })
    rest = rest.slice(m[0].length)
  }
  if (!rest) return out
  if (/^['"]/.test(rest)) out.push({ kind: 'string', text: rest })
  else if (/^-?\d+(\.\d+)?$/.test(rest)) out.push({ kind: 'number', text: rest })
  else out.push({ kind: 'value', text: rest })
  return out
}

export function tokenizeYaml(yaml: string): YamlToken[][] {
  return yaml.split('\n').map(tokenizeYamlLine)
}
